import { useState } from 'react'
import { ArrowLeft, Eye, EyeOff, Loader2, AlertTriangle, Heart, Mail } from 'lucide-react'
import { cancelSubscription } from '../lib/stripe'
import { track, AnalyticsEvents } from '../lib/mixpanel'

interface SettingsScreenProps {
  isPro: boolean
  isAuthenticated: boolean
  onBack: () => void
  onUpdatePassword: (newPassword: string) => Promise<void>
  onRefreshProfile: () => Promise<void>
  onDeleteAllNotes: () => Promise<void>
}

export function SettingsScreen({ isPro, isAuthenticated, onBack, onUpdatePassword, onRefreshProfile, onDeleteAllNotes }: SettingsScreenProps) {
  // Password form state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Cancel subscription state
  const [showCancelWarning, setShowCancelWarning] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [cancelSuccess, setCancelSuccess] = useState(false)

  // Delete all notes state
  const [showDeleteAllWarning, setShowDeleteAllWarning] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(false)

    if (!newPassword.trim()) {
      setPasswordError('Please enter a new password.')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.')
      return
    }

    setPasswordLoading(true)
    try {
      await onUpdatePassword(newPassword)
      track(AnalyticsEvents.PASSWORD_CHANGED)
      setPasswordSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update password.'
      setPasswordError(message)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    setCancelError(null)
    setCancelLoading(true)
    try {
      await cancelSubscription()
      track(AnalyticsEvents.SUBSCRIPTION_CANCELLED)
      setCancelSuccess(true)
      setShowCancelWarning(false)
      // Refresh profile to update isPro state
      await onRefreshProfile()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription.'
      setCancelError(message)
    } finally {
      setCancelLoading(false)
    }
  }

  return (
    <div
      className="h-screen flex flex-col"
      style={{ backgroundColor: 'hsl(var(--color-bg-primary))' }}
    >
      {/* Back button */}
      <div className="px-5 py-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium transition-fast hover:opacity-80"
          style={{ color: 'hsl(var(--color-text-secondary))' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex items-start justify-center px-5 pt-8 overflow-y-auto">
        <div className="w-full max-w-sm pb-12">
          {/* Heading */}
          <h1
            className="text-2xl font-bold mb-8"
            style={{ color: 'hsl(var(--color-text-primary))' }}
          >
            Settings
          </h1>

          {/* Change Password Section */}
          <section className="mb-10">
            <h2
              className="text-sm font-semibold uppercase tracking-wider mb-4"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              Change Password
            </h2>

            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              {/* New password */}
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="New password"
                  autoComplete="new-password"
                  disabled={passwordLoading}
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-fast placeholder:opacity-50"
                  style={{
                    backgroundColor: 'hsl(var(--color-bg-secondary))',
                    color: 'hsl(var(--color-text-primary))',
                    border: '1px solid hsl(var(--color-border))',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-fast hover:opacity-80"
                  style={{ color: 'hsl(var(--color-text-muted))' }}
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Confirm password */}
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  disabled={passwordLoading}
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-fast placeholder:opacity-50"
                  style={{
                    backgroundColor: 'hsl(var(--color-bg-secondary))',
                    color: 'hsl(var(--color-text-primary))',
                    border: '1px solid hsl(var(--color-border))',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-fast hover:opacity-80"
                  style={{ color: 'hsl(var(--color-text-muted))' }}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Error */}
              {passwordError && (
                <p className="text-sm px-1" style={{ color: 'hsl(var(--color-error))' }}>
                  {passwordError}
                </p>
              )}

              {/* Success */}
              {passwordSuccess && (
                <p className="text-sm px-1" style={{ color: 'hsl(var(--color-success))' }}>
                  Password updated successfully.
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-fast hover:opacity-90 disabled:opacity-60"
                style={{
                  backgroundColor: 'hsl(var(--color-accent))',
                  color: 'hsl(var(--color-bg-primary))',
                }}
              >
                {passwordLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Update Password
              </button>
            </form>
          </section>

          {/* Delete All Notes Section (only for signed-in users) */}
          {isAuthenticated && (
            <section className="mb-10">
              <h2
                className="text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: 'hsl(var(--color-text-muted))' }}
              >
                Danger Zone
              </h2>

              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: 'hsl(var(--color-bg-secondary))' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'hsl(var(--color-text-primary))' }}
                    >
                      Delete All Notes
                    </p>
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: 'hsl(var(--color-text-secondary))' }}
                    >
                      Permanently delete all your notes
                    </p>
                  </div>
                  {!showDeleteAllWarning && (
                    <button
                      onClick={() => setShowDeleteAllWarning(true)}
                      className="text-sm font-medium transition-fast hover:opacity-80"
                      style={{ color: 'hsl(var(--color-error))' }}
                    >
                      Delete all
                    </button>
                  )}
                </div>

                {showDeleteAllWarning && (
                  <div
                    className="mt-3 rounded-xl p-4 border"
                    style={{
                      backgroundColor: 'hsl(var(--color-error) / 0.05)',
                      borderColor: 'hsl(var(--color-error) / 0.2)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                        style={{ color: 'hsl(var(--color-error))' }}
                      />
                      <div>
                        <p
                          className="text-sm font-medium mb-1"
                          style={{ color: 'hsl(var(--color-text-primary))' }}
                        >
                          Are you sure?
                        </p>
                        <p
                          className="text-xs mb-4"
                          style={{ color: 'hsl(var(--color-text-secondary))' }}
                        >
                          This will permanently delete all your notes. This action cannot be undone.
                        </p>
                        {deleteError && (
                          <p className="text-xs mb-3" style={{ color: 'hsl(var(--color-error))' }}>
                            {deleteError}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              setDeleteError(null)
                              setDeleteLoading(true)
                              try {
                                await onDeleteAllNotes()
                                setShowDeleteAllWarning(false)
                                onBack()
                              } catch (err) {
                                const message = err instanceof Error ? err.message : 'Failed to delete notes.'
                                setDeleteError(message)
                              } finally {
                                setDeleteLoading(false)
                              }
                            }}
                            disabled={deleteLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-fast hover:opacity-90 disabled:opacity-60"
                            style={{
                              backgroundColor: 'hsl(var(--color-error))',
                              color: '#fff',
                            }}
                          >
                            {deleteLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                            {deleteLoading ? 'Deleting...' : 'Yes, delete all'}
                          </button>
                          <button
                            onClick={() => setShowDeleteAllWarning(false)}
                            disabled={deleteLoading}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-fast hover:opacity-80 disabled:opacity-60"
                            style={{ color: 'hsl(var(--color-text-secondary))' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Subscription Section (only for Pro users) */}
          {isPro && (
            <section>
              <h2
                className="text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: 'hsl(var(--color-text-muted))' }}
              >
                Subscription
              </h2>

              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: 'hsl(var(--color-bg-secondary))' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'hsl(var(--color-text-primary))' }}
                    >
                      JotApp Pro
                    </p>
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: 'hsl(var(--color-text-secondary))' }}
                    >
                      $5/month
                    </p>
                  </div>
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: 'hsl(var(--color-success) / 0.12)',
                      color: 'hsl(var(--color-success))',
                    }}
                  >
                    Active
                  </span>
                </div>

                {/* Cancel error */}
                {cancelError && (
                  <p className="text-sm mb-3" style={{ color: 'hsl(var(--color-error))' }}>
                    {cancelError}
                  </p>
                )}

                {/* Cancel success */}
                {cancelSuccess && (
                  <p className="text-sm mb-3" style={{ color: 'hsl(var(--color-warning))' }}>
                    Your subscription has been cancelled. You'll have access until the end of your billing period.
                  </p>
                )}

                {!cancelSuccess && !showCancelWarning && (
                  <button
                    onClick={() => setShowCancelWarning(true)}
                    className="text-sm font-medium transition-fast hover:opacity-80"
                    style={{ color: 'hsl(var(--color-error))' }}
                  >
                    Cancel subscription
                  </button>
                )}

                {/* Cancel warning dialog */}
                {showCancelWarning && !cancelSuccess && (
                  <div
                    className="mt-3 rounded-xl p-4 border"
                    style={{
                      backgroundColor: 'hsl(var(--color-error) / 0.05)',
                      borderColor: 'hsl(var(--color-error) / 0.2)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                        style={{ color: 'hsl(var(--color-error))' }}
                      />
                      <div>
                        <p
                          className="text-sm font-medium mb-1"
                          style={{ color: 'hsl(var(--color-text-primary))' }}
                        >
                          Are you sure?
                        </p>
                        <p
                          className="text-xs mb-4"
                          style={{ color: 'hsl(var(--color-text-secondary))' }}
                        >
                          Your subscription will end at the close of this billing period. Sync stays free either way.
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleCancelSubscription}
                            disabled={cancelLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-fast hover:opacity-90 disabled:opacity-60"
                            style={{
                              backgroundColor: 'hsl(var(--color-error))',
                              color: '#fff',
                            }}
                          >
                            {cancelLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                            Yes, cancel
                          </button>
                          <button
                            onClick={() => setShowCancelWarning(false)}
                            disabled={cancelLoading}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-fast hover:opacity-80 disabled:opacity-60"
                            style={{ color: 'hsl(var(--color-text-secondary))' }}
                          >
                            Keep subscription
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
          {/* About Section */}
          <section className="mt-10">
            <h2
              className="text-sm font-semibold uppercase tracking-wider mb-4"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              About
            </h2>

            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: 'hsl(var(--color-bg-secondary))' }}
            >
              {/* App name */}
              <div className="flex items-center gap-2 mb-3">
                <p
                  className="text-sm font-bold tracking-tight"
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: 'hsl(var(--color-text-primary))',
                  }}
                >
                  JOT
                </p>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: 'hsl(var(--color-bg-tertiary))',
                    color: 'hsl(var(--color-text-muted))',
                  }}
                >
                  v1.0
                </span>
              </div>

              {/* Made with love */}
              <p
                className="text-sm flex items-center gap-1.5"
                style={{ color: 'hsl(var(--color-text-secondary))' }}
              >
                Made with
                <Heart
                  className="w-3.5 h-3.5 fill-current"
                  style={{ color: 'hsl(var(--color-error))' }}
                />
                in India
              </p>

              {/* Divider */}
              <div
                className="my-3 h-px"
                style={{ backgroundColor: 'hsl(var(--color-border))' }}
              />

              {/* Support email */}
              <a
                href="mailto:justgojot@gmail.com"
                className="flex items-center gap-2.5 text-sm font-medium transition-fast hover:opacity-80"
                style={{ color: 'hsl(var(--color-accent))' }}
              >
                <Mail className="w-4 h-4" />
                justgojot@gmail.com
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
